import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import ProductsPage from './modules/products/pages/ProductsPage'
import CustomersPage from './components/CustomersPage'
import TopUpPage from './modules/order/pages/TopUpPage'
import UtilitiesPage from './components/UtilitiesPage'
import ProfilePage from './modules/user/pages/ProfilePage'
import PolicyContactPage from './components/PolicyContactPage'

import PaymentReturnPage from './modules/order/pages/PaymentReturnPage'
import ChatWidget from './modules/chat/components/ChatWidget'
import RecentPurchasesNotification from './components/RecentPurchasesNotification'
import SnowEffect from './components/SnowEffect'
import StarsEffect from './components/StarsEffect'
import FireworksEffect from './components/FireworksEffect'

import { useAuth } from './modules/auth/hooks/useAuth'
import { AuthProvider } from './modules/auth/context/AuthProvider'
import { useEffect, useState } from 'react'
import { setupAntiDebug } from './lib/antiDebug'
import { ToastProvider } from './lib/toast'
import UserEventsBridge from './modules/realtime/UserEventsBridge'
import CustomerLayout from './layouts/CustomerLayout'
import VisualSidebarsPage from './visual/VisualSidebarsPage'
import { MotionConfig } from 'framer-motion'

function NavigateToProductsPreserveQuery() {
  const location = useLocation()
  return <Navigate to={{ pathname: '/products', search: location.search }} replace />
}

function AppInner() {
  const isVisual = typeof window !== 'undefined' && window.location.pathname.startsWith('/__visual__/')
  const { loading, isRetrying, connectAttempt, connectMaxAttempts, connectError } = useAuth()
  const [isEffectsOn, setIsEffectsOn] = useState(false) // Default OFF

  useEffect(() => {
    // Initialize from localStorage
    const stored = window.localStorage.getItem('digigo-effects')
    if (stored === 'on') {
      setIsEffectsOn(true)
    }

    const onEffectsChanged = (ev: Event) => {
      const isOn = (ev as CustomEvent<{ isEffectsOn: boolean }>).detail?.isEffectsOn
      if (typeof isOn === 'boolean') setIsEffectsOn(isOn)
    }
    window.addEventListener('digigo-effects-changed', onEffectsChanged)
    return () => window.removeEventListener('digigo-effects-changed', onEffectsChanged)
  }, [])

  useEffect(() => {
    document.documentElement.classList.toggle('digigo-effects-off', !isEffectsOn)
    return () => document.documentElement.classList.remove('digigo-effects-off')
  }, [isEffectsOn])

  useEffect(() => {
    if (isVisual) return
    const cleanup = setupAntiDebug({ bypass: false })
    return () => { if (cleanup) cleanup() }
  }, [isVisual])

  if (loading && !isVisual) {
    return (
      <div className="min-h-screen app-bg flex flex-col items-center justify-center px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4" />
        <div className="text-center">
          {isRetrying && connectAttempt > 0 ? (
            <>
              <p className="text-slate-700 dark:text-slate-200 font-semibold">Đang kết nối backend...</p>
              <p className="text-sm text-slate-500 dark:text-slate-300">Lần thử {connectAttempt}/{connectMaxAttempts}</p>
            </>
          ) : null}
          {connectError ? (
            <p className="text-sm text-red-600 mt-2 max-w-md">{connectError}</p>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <MotionConfig reducedMotion={isEffectsOn ? 'never' : 'always'}>
      <ToastProvider>
        <Router>
          <UserEventsBridge />
          <Routes>
            <Route path="__visual__/sidebars" element={<VisualSidebarsPage />} />
            <Route element={<CustomerLayout />}>
              <Route index element={<NavigateToProductsPreserveQuery />} />
              <Route path="product" element={<NavigateToProductsPreserveQuery />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="topup" element={<TopUpPage />} />
              <Route path="purchased" element={<Navigate to="/profile#purchases" replace />} />
              <Route path="utilities" element={<UtilitiesPage />} />
              <Route path="policy" element={<PolicyContactPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="payment-return" element={<PaymentReturnPage />} />
              <Route path="*" element={<NavigateToProductsPreserveQuery />} />
            </Route>
          </Routes>

          {!isVisual ? (
            <>
              <ChatWidget />
              <RecentPurchasesNotification />

              {isEffectsOn ? (
                <>
                  <FireworksEffect />
                  <StarsEffect />
                  <SnowEffect />
                </>
              ) : null}
            </>
          ) : null}
        </Router>
      </ToastProvider>
    </MotionConfig>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  )
}

export default App
